import { computed, ref, watch } from 'vue'
import { useModel } from './model.ts'
import { useLoadInventoryFiles } from './api.ts'
import { useHelpers } from '@/shared/lib'

type UploadObjectFilesOptions = {
  draftMode?: boolean
  onDraftFilesSelected?: (files: File[]) => void
}

/**
 * Автозагрузка выбранных файлов объекта инвентаризации.
 * Валидация и сжатие — до отправки на сервер.
 */
export const useUploadObjectFiles = (
  getObjectId: () => number,
  onFilesUploaded: (files: unknown[]) => void,
  options: UploadObjectFilesOptions = {},
) => {
  const model = useModel()
  const { uploadedFiles, rules, compressImages, isCompressing } = model
  const { upload, isPending } = useLoadInventoryFiles()
  const { formatFileSize } = useHelpers()
  const isProcessing = ref(false)

  const validateFiles = (files: File[]): boolean =>
    rules.files.every((rule) => rule(files) === true)

  const uploadSelectedFiles = async (files: File[]) => {
    if (!files.length || isProcessing.value) return

    if (!validateFiles(files)) return

    isProcessing.value = true

    try {
      const compressed = await compressImages(files)

      if (options.draftMode && !getObjectId()) {
        options.onDraftFilesSelected?.(compressed)
        uploadedFiles.value = []
        return
      }

      const payload = {
        id: getObjectId(),
        files: new FormData(),
      }

      compressed.forEach((item) => {
        const splitName = item.name.split('.')
        const ext = splitName.pop() || ''

        payload.files.append('files[]', item)
        payload.files.append('file_names[]', splitName.join('.'))
        payload.files.append('file_ext[]', ext)
        payload.files.append('file_sizes[]', formatFileSize(item.size))
      })

      const result = await upload(payload)
      if (result?.data?.files) {
        onFilesUploaded(result.data.files)
        uploadedFiles.value = []
      }
    } finally {
      isProcessing.value = false
    }
  }

  watch(uploadedFiles, (files) => {
    if (files.length) {
      void uploadSelectedFiles(files)
    }
  })

  const isLoading = computed(() => isCompressing.value || isPending.value || isProcessing.value)

  return {
    ...model,
    isLoading,
  }
}
